<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);

const iframeSrc = ref('');
const iframeRef = ref(null);
let darkObs = null;

function isDarkActive() {
  return document.body.classList.contains('dark');
}

function buildSrc() {
  const acctId = accountId.value || '0';
  const agentName = currentUser.value?.available_name || currentUser.value?.name || '';
  const dark = isDarkActive() ? '1' : '0';

  let cwToken = '', cwClient = '', cwUid = '';
  try {
    const cookies = document.cookie.split(';');
    for (const ck of cookies) {
      const co = ck.trim();
      if (co.startsWith('cw_d_session_info')) {
        const val = decodeURIComponent(co.substring(co.indexOf('=') + 1));
        const sess = JSON.parse(val);
        cwToken = sess['access-token'] || '';
        cwClient = sess['client'] || '';
        cwUid = sess['uid'] || '';
        break;
      }
    }
  } catch {}
  if (!cwToken) cwToken = currentUser.value?.access_token || '';

  let src = `/twilio-voice/chamadas?account_id=${acctId}&instance=brasa&dark=${dark}`;
  if (agentName) src += `&agent=${encodeURIComponent(agentName)}`;
  if (cwToken) src += `&token=${encodeURIComponent(cwToken)}&cw_token=${encodeURIComponent(cwToken)}`;
  if (cwClient) src += `&cw_client=${encodeURIComponent(cwClient)}`;
  if (cwUid) src += `&cw_uid=${encodeURIComponent(cwUid)}`;
  return src;
}

function sendDarkMode() {
  try {
    if (iframeRef.value && iframeRef.value.contentWindow) {
      iframeRef.value.contentWindow.postMessage({ type: 'sz-dark-mode', dark: isDarkActive() }, '*');
    }
  } catch {}
}

function onIframeLoad() {
  // When iframe loads, send dark mode state multiple times to ensure delivery
  sendDarkMode();
  setTimeout(sendDarkMode, 200);
  setTimeout(sendDarkMode, 600);
}

// Watch for accountId to become available, then build URL once
watch([accountId, currentUser], () => {
  if (!iframeSrc.value && accountId.value && accountId.value !== '0' && currentUser.value?.access_token) {
    setTimeout(() => {
      iframeSrc.value = buildSrc();
    }, 300);
  }
}, { immediate: true });

onMounted(() => {
  // Watch body class changes - ONLY send postMessage, never rebuild URL
  darkObs = new MutationObserver(() => sendDarkMode());
  darkObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  // Listen for iframe asking for dark state
  window.addEventListener('message', (e) => {
    if (e.data && e.data.type === 'sz-dark-mode-query') sendDarkMode();
  });
});

onBeforeUnmount(() => {
  if (darkObs) darkObs.disconnect();
});
</script>

<template>
  <div class="sz-chamadas-page">
    <iframe
      v-if="iframeSrc"
      ref="iframeRef"
      :src="iframeSrc"
      class="sz-chamadas-iframe"
      allow="autoplay; microphone"
      frameborder="0"
      @load="onIframeLoad"
    />
  </div>
</template>

<style scoped>
.sz-chamadas-page {
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.sz-chamadas-iframe {
  width: 100%;
  height: 100%;
  border: none;
  flex: 1;
}
</style>
